import type { ITenant } from '@experiences/interfaces';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    makeStyles,
    useTheme,
} from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApDropdown,
    ApDropdownItem,
    ApIconButton,
    ApTooltip,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import type { Folder } from '../../../../services/orchestrator/OrchFolderService';
import {
    FolderType,
    getAllFoldersForCurrentUser,
} from '../../../../services/orchestrator/OrchFolderService';
import { accountGlobalId } from '../../../../store/selectors';

interface TooltipProps {
    text: string;
    placement?: 'top' | 'right' | 'bottom' | 'left' | 'top-start';
}

interface FolderDropdownProps {
    tenant?: ITenant;
    value?: Folder;
    required?: boolean;
    disableAutoSelectFirstOption?: boolean;
    onChange: (newFolder?: Folder) => void;
    labelTooltip?: TooltipProps;
    disabled?: boolean;
}

interface InnerDropdownProps {
    tenant?: ITenant;
    isFoldersLoading: boolean;
    availableFolders?: Folder[];
    selectedValue?: string;
    handleOnChange: (event: CustomEvent) => void;
    disabled?: boolean;
    required?: boolean;
    label?: string;
}

const useStyles = makeStyles(_ =>
    createStyles({
        tooltipIcon: {
            width: '18px',
            height: '18px',
        },
    }),
);

const InnerDropdown: React.FC<InnerDropdownProps> = ({
    tenant, isFoldersLoading, availableFolders, selectedValue, handleOnChange, disabled, required, label,
}: InnerDropdownProps) => (
    <ApDropdown
        style={{ width: '240px' }}
        label={label}
        required={required}
        selectedValue={selectedValue}
        onSelectedValueChanged={handleOnChange}
        data-cy="llm-configurations-folder-dropdown"
        disabled={!!disabled || !tenant?.id || isFoldersLoading}>
        {availableFolders?.map((folder) => (
            <ApDropdownItem
                value={folder.Key}
                label={folder.FullyQualifiedName}
                key={`ap-dropdown-item-${folder.Key}`}
                data-cy={`ap-dropdown-item-${folder.Key}`} />
        ))}
    </ApDropdown>
);

export const FolderDropdown: React.FC<FolderDropdownProps> = ({
    tenant, value, disabled, required, disableAutoSelectFirstOption, onChange, labelTooltip,
}: FolderDropdownProps) => {
    const { formatMessage: translate } = useIntl();
    const theme = useTheme();
    const classes = useStyles();
    const organizationId = useSelector(accountGlobalId);

    const [ selectedValue, setSelectedValue ] = useState<string | undefined>(value?.Key);

    const {
        data: availableFolders, isValidating: isFoldersLoading,
    } = useSWR(
        tenant?.id ? [ organizationId, tenant?.id ] : undefined,
        () => getAllFoldersForCurrentUser({
            accountLogicalName: organizationId,
            tenantName: tenant?.id ?? '',
        }).then((response) => response.PageItems.filter(f => f.FolderType !== FolderType.Personal)),
    );

    const handleOnChange = useCallback((event: CustomEvent) => {
        const newValue = event.detail as string;
        const folder = availableFolders?.find((f) => f.Key === newValue);
        setSelectedValue(newValue);
        onChange(folder);
    }, [ onChange, availableFolders ]);

    useEffect(() => {
        // Set the first folder as the default value if no folder is provided
        if (!disableAutoSelectFirstOption && !selectedValue && availableFolders && availableFolders.length > 0) {
            const defaultFolder = availableFolders[0];
            setSelectedValue(defaultFolder.Key);
            onChange(defaultFolder);
        }
    }, [ selectedValue, availableFolders, onChange, disableAutoSelectFirstOption ]);

    useEffect(() => {
        // Update the selected value when the prop value changes
        setSelectedValue(value?.Key);
    }, [ value ]);

    return labelTooltip ? (
        <UiStack
            direction='column'
        >
            <UiStack
                direction='row'
                align='center'
                mb={SpacingToken.Micro}
                data-cy="llm-configurations-folder"
            >
                <UiText
                    variant={FontVariantToken.fontSizeMBold}
                    color={theme.palette.semantic.colorForegroundDeEmp}
                >
                    {translate({ id: 'CLIENT_TABLE_HEADER_FOLDER' })}
                    {required ? ' *' : ''}
                </UiText>
                <ApTooltip
                    placement={labelTooltip.placement}
                    content={labelTooltip.text}
                >
                    <ApIconButton
                        data-cy='llm-configurations-folder-tooltip'
                    >
                        <InfoOutlinedIcon
                            className={classes.tooltipIcon}
                            color='action'
                            aria-label={labelTooltip.text} />
                    </ApIconButton>
                </ApTooltip>
            </UiStack>

            <InnerDropdown
                tenant={tenant}
                isFoldersLoading={isFoldersLoading}
                availableFolders={availableFolders}
                selectedValue={selectedValue}
                handleOnChange={handleOnChange}
                disabled={disabled}
                required={required}
            />
        </UiStack>
    ) : (
        <InnerDropdown
            tenant={tenant}
            isFoldersLoading={isFoldersLoading}
            availableFolders={availableFolders}
            selectedValue={selectedValue}
            handleOnChange={handleOnChange}
            disabled={disabled}
            required={required}
            label={translate({ id: 'CLIENT_TABLE_HEADER_FOLDER' })}
        />
    );
};
